/* eslint-disable react-hooks/exhaustive-deps */
import { forwardRef, useImperativeHandle, useState } from 'react';
import PropTypes from 'prop-types';
import { Box, Typography, TextField } from '@mui/material';
import { useForm } from 'react-hook-form';
import useSnackbar from 'src/hooks/useCustomSnackbar';
import { Iconify } from 'src/components';
import { STEP_BADGE, SECTION_CARD, chipStyle, SECTION_HEADER_LABEL, SECTION_ICON_COLOR, SUB_LABEL as SUB_LABEL_STYLE } from 'src/styles/stepFormStyles';
import { FormProvider, RHFTextField } from 'src/components/hook-form';
import { useSelector, dispatch } from 'src/redux/store';
import { selectFollowLead } from 'src/redux/selectors/leadSelectors';
// ContactRound imports removed — note path handles count increment
import { closeWhatsappConversationWithReason } from 'src/redux/slices/whatsappV2';
import { callApi } from 'src/utils/apiCall';
import { markAsSpam as markLeadAsSpam } from 'src/redux/slices/spamLeads';

const CLOSE_REASONS = [
    { value: 'NOT_INTERESTED', label: 'Not interested' },
    { value: 'TOO_EXPENSIVE', label: 'Too expensive' },
    { value: 'ANOTHER_PRACTICE', label: 'Another practice' },
    { value: 'CHANGED_MIND', label: 'Changed mind' },
    { value: 'WANT_NHS', label: 'Want NHS' },
    { value: 'CLINICALLY_NOT_SUITABLE', label: 'Clinically not suitable' },
    { value: 'DUPLICATE_ENTRY', label: 'Duplicate' },
];

const FLAG_REASONS = [
    { value: 'SPAM', label: 'Spam' },
];

const JUST_CLOSE_REASONS = [
    { value: 'GENERAL_CHAT', label: 'General chat' },
    { value: 'RESOLVED', label: 'Resolved' },
];

// eslint-disable-next-line no-unused-vars
const WhatsAppCloseStep = forwardRef(({ handleNext, handleClose, onWhatsAppClose }, ref) => {
    const { enqueueSnackbar } = useSnackbar();
    const { practiceId } = useSelector((state) => state.practiceTreatmentList);
    const followLead = useSelector(selectFollowLead);
    const whatsAppThread = useSelector((state) => state.whatsappV2?.whatsAppChatMetaData?.thread);

    const [closeReason, setCloseReason] = useState('');
    const [calledConfirmed, setCalledConfirmed] = useState(null);
    const [selectedCallOptions, setSelectedCallOptions] = useState([]);
    const [callNote, setCallNote] = useState('');
    const [isSaving, setIsSaving] = useState(false);

    const leadId = followLead?.id;
    const treatmentId = followLead?.lead_treatments?.[0]?.id;

    const methods = useForm({ defaultValues: { notes: '' } });
    const { handleSubmit, watch } = methods;
    const notes = watch('notes');

    const isArchive = CLOSE_REASONS.some(r => r.value === closeReason);
    const isFlag = FLAG_REASONS.some(r => r.value === closeReason);
    const reasonLabel = [...CLOSE_REASONS, ...FLAG_REASONS, ...JUST_CLOSE_REASONS].find(r => r.value === closeReason)?.label || closeReason;

    const onSubmit = async () => {
        if (!closeReason || isSaving) return;
        setIsSaving(true);
        try {
            if (leadId && practiceId) {
                if (closeReason === 'SPAM') {
                    await dispatch(markLeadAsSpam({ practiceId, leadId, handleClose: () => {} }));
                } else {
                    let noteType = 'OTHER';
                    if (isArchive) noteType = closeReason;
                    else if (isFlag) noteType = closeReason;
                    // Create the note for audit trail + timeline first
                    await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/notes/`, 'post', {
                        note: noteType, additional_notes: notes || `WhatsApp closed: ${reasonLabel}`, lead: leadId,
                        contacted_via: calledConfirmed ? 'PHONE' : 'WHATSAPP',
                        ...(treatmentId ? { treatment: treatmentId } : {}),
                    });
                    // Actually archive the lead when an archive reason is selected
                    if (isArchive) {
                        await callApi(`/api/v1/practices/${practiceId}/leads/${leadId}/archive/`, 'post', {
                            reason: closeReason,
                            note: notes || `WhatsApp closed: ${reasonLabel}`,
                        });
                    }
                }
            }

            const threadId = whatsAppThread?.id;
            if (threadId && leadId) dispatch(closeWhatsappConversationWithReason(threadId, reasonLabel, leadId));
            if (onWhatsAppClose) onWhatsAppClose(reasonLabel);

            let msg = `Closed: ${reasonLabel}`;
            if (isArchive) msg = `Archived: ${reasonLabel}`;
            else if (isFlag) msg = `Flagged as ${reasonLabel}`;
            enqueueSnackbar(msg, { variant: 'success', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
            handleClose('finish');
        } catch (err) {
            console.error('[WhatsAppCloseStep] submit error:', err);
            enqueueSnackbar('Failed to close conversation. Please try again.', { variant: 'error', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
        } finally {
            setIsSaving(false);
        }
    };

    const isValid = calledConfirmed !== null && !!closeReason;

    useImperativeHandle(ref, () => ({
        async submit() {
            if (!isValid) {
                enqueueSnackbar(calledConfirmed === null ? 'Please select a contact method' : 'Please select a close reason', { variant: 'warning', anchorOrigin: { vertical: 'top', horizontal: 'center' } });
                return;
            }
            await handleSubmit(onSubmit)();
        },
    }), [closeReason, calledConfirmed, isSaving, isValid, onSubmit, handleSubmit]);

    const BADGE = STEP_BADGE;
    const CARD = SECTION_CARD;
    const CHIP = chipStyle;
    const SUB_LABEL = { ...SUB_LABEL_STYLE, color: '#C4CDD5' };

    return (
        <FormProvider methods={methods} onSubmit={handleSubmit(onSubmit)}>
            <Box sx={{ px: 3, py: 2 }}>

                {/* Step 1: Contact Method */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={BADGE}>1</Box>
                        <Iconify icon="eva:phone-fill" width={16} sx={{ color: SECTION_ICON_COLOR }} />
                        <Typography sx={SECTION_HEADER_LABEL}>Contact method</Typography>
                    </Box>
                    <Box sx={{ ...CARD, display: 'flex', gap: 1 }}>
                        <Box onClick={() => setCalledConfirmed(true)} sx={CHIP(calledConfirmed === true, false)}>
                            <Iconify icon="eva:phone-fill" width={16} sx={{ mr: 0.75 }} />
                            Yes, I called
                        </Box>
                        <Box onClick={() => setCalledConfirmed(false)} sx={CHIP(calledConfirmed === false, true)}>
                            <Iconify icon="ri:whatsapp-fill" width={16} sx={{ mr: 0.75 }} />
                            WhatsApp only
                        </Box>
                    </Box>
                    {/* Call outcome — only when "Yes, I called" */}
                    {calledConfirmed === true && (
                        <Box sx={{ ...CARD, mt: 1 }}>
                            <Typography sx={{ fontSize: '10px', color: '#919EAB', textTransform: 'uppercase', letterSpacing: '0.3px', fontWeight: 600, mb: 0.75 }}>Leave a note about the call attempt</Typography>
                            <TextField size="small" fullWidth multiline rows={2} value={callNote} onChange={(e) => setCallNote(e.target.value)}
                                placeholder="Enter or select details about the call attempt"
                                sx={{ mb: 1, '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '12px' } }} />
                            <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
                                {['No answer', 'Left voicemail', 'Incorrect number', 'Call declined', 'Poor call quality', 'Reached different person', 'Busy line', 'Requested call-back'].map((opt) => (
                                    <Box key={opt} onClick={() => {
                                        const newSelected = selectedCallOptions.includes(opt) ? selectedCallOptions.filter(v => v !== opt) : [...selectedCallOptions, opt];
                                        setSelectedCallOptions(newSelected);
                                        setCallNote(newSelected.join(', '));
                                    }} sx={CHIP(selectedCallOptions.includes(opt), false)}>{opt}</Box>
                                ))}
                            </Box>
                        </Box>
                    )}
                </Box>

                {/* Step 2: Close reason */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={BADGE}>2</Box>
                        <Iconify icon="eva:flag-fill" width={16} sx={{ color: SECTION_ICON_COLOR }} />
                        <Typography sx={SECTION_HEADER_LABEL}>Reason</Typography>
                    </Box>
                    <Box sx={CARD}>
                    <Typography sx={SUB_LABEL}>Archive</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', mb: 2 }}>
                        {CLOSE_REASONS.map((opt) => (
                            <Box key={opt.value} onClick={() => setCloseReason(opt.value)} sx={CHIP(closeReason === opt.value, false)}>
                                {opt.label}
                            </Box>
                        ))}
                    </Box>

                    <Typography sx={SUB_LABEL}>Flag</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75, mb: 2 }}>
                        {FLAG_REASONS.map((opt) => (
                            <Box key={opt.value} onClick={() => setCloseReason(opt.value)} sx={CHIP(closeReason === opt.value, false)}>
                                {opt.label}
                            </Box>
                        ))}
                    </Box>

                    <Typography sx={SUB_LABEL}>Just close</Typography>
                    <Box sx={{ display: 'flex', gap: 0.75 }}>
                        {JUST_CLOSE_REASONS.map((opt) => (
                            <Box key={opt.value} onClick={() => setCloseReason(opt.value)} sx={CHIP(closeReason === opt.value, false)}>
                                {opt.label}
                            </Box>
                        ))}
                    </Box>
                    </Box>
                </Box>

                {/* Step 3: Notes */}
                <Box sx={{ mb: 2.5 }}>
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, mb: 1.25 }}>
                        <Box sx={BADGE}>3</Box>
                        <Iconify icon="eva:edit-2-fill" width={16} sx={{ color: SECTION_ICON_COLOR }} />
                        <Typography sx={SECTION_HEADER_LABEL}>Notes</Typography>
                    </Box>
                    <Box sx={CARD}>
                    <RHFTextField name="notes" multiline rows={3} placeholder="Add a note..." InputLabelProps={{ shrink: true }}
                        sx={{ '& .MuiOutlinedInput-root': { borderRadius: '10px', fontSize: '13px' } }} />
                    </Box>
                </Box>
            </Box>
        </FormProvider>
    );
});

WhatsAppCloseStep.propTypes = {
    handleNext: PropTypes.func,
    handleClose: PropTypes.func,
    onWhatsAppClose: PropTypes.func,
};

export default WhatsAppCloseStep;
